/**
 * Market-Aware Cart Utilities
 * Provides locale-aware cart API functions to prevent race conditions and ensure correct market URLs
 */

class MarketCartUtils {
  constructor() {
    this.marketDetectionReady = false;
    this.currentMarket = null;
    // Global throttling for cart.js requests
    this.pendingRequest = null;
    this.lastRequestTime = 0;
    this.minRequestInterval = 300; // Increased to 300ms between requests
    // Event-based cart data cache (from recent events)
    this.eventCartData = null;
    this.eventCartDataTime = 0;
    this.eventCartDataExpiry = 5000; // 5 seconds

    // Cart mutation queue to prevent race conditions
    this.mutationQueue = [];
    this.isProcessingMutation = false;
    this.mutationLock = null;

    // Rate limit handling
    this.rateLimitBackoff = 2000; // Linear retry gaps: 2s, 4s, 6s
    this.maxBackoff = 10000; // Max 10 second backoff
    this.consecutiveErrors = 0;

    // After checkout validation rejects an add (422), skip repeat /cart/add.js calls
    // until a cart mutation succeeds (same message, no network round-trip).
    this.checkoutValidationBlockMessage = null;

    // Track pending add operations to prevent duplicates
    this.pendingAddOperations = new Map();

    // Quantities of adds that have passed the inventory guard but haven't landed
    // in the cart yet, keyed by variant ID. Counted alongside the cart so two
    // rapid adds can't both be cleared against the same units of stock.
    this.pendingAddQuantities = new Map();

    // Inventory guard: only variants at or below this stock level are checked
    // against the cart before adding. Above it, double-adding can't oversell in
    // practice and the check would cost every shopper a /cart.js round trip.
    this.inventoryGuardThreshold = 20;

    this.init();
  }

  /**
   * Animate every visible cart icon in the header (multi-market headers
   * render duplicate `#cart-icon` elements, so we iterate all of them).
   */
  shakeCartIcon() {
    const icons = document.querySelectorAll('[id="cart-icon"]');
    icons.forEach((icon) => {
      try { icon.getAnimations && icon.getAnimations().forEach((a) => a.cancel()); } catch (e) {}
      icon.style.transformOrigin = 'center center';
      icon.animate(
        [
          { transform: 'scale(1) rotate(0deg)' },
          { transform: 'scale(1.2) rotate(-15deg)', offset: 0.1 },
          { transform: 'scale(1.2) rotate(15deg)',  offset: 0.2 },
          { transform: 'scale(1.2) rotate(-12deg)', offset: 0.3 },
          { transform: 'scale(1.2) rotate(12deg)',  offset: 0.4 },
          { transform: 'scale(1.2) rotate(-8deg)',  offset: 0.5 },
          { transform: 'scale(1.2) rotate(8deg)',   offset: 0.6 },
          { transform: 'scale(1.2) rotate(-4deg)',  offset: 0.7 },
          { transform: 'scale(1.2) rotate(4deg)',   offset: 0.8 },
          { transform: 'scale(1) rotate(0deg)',     offset: 1 }
        ],
        { duration: 900, easing: 'cubic-bezier(0.455, 0.03, 0.515, 0.955)', fill: 'none' }
      );
    });
  }

  /**
   * Animate every visible cart icon in the header (multi-market headers
   * render duplicate `#cart-icon` elements, so we iterate all of them).
   */
  shakeCartIcon() {
    const icons = document.querySelectorAll('[id="cart-icon"]');
    icons.forEach((icon) => {
      try { icon.getAnimations && icon.getAnimations().forEach((a) => a.cancel()); } catch (e) {}
      icon.style.transformOrigin = 'center center';
      icon.animate(
        [
          { transform: 'scale(1) rotate(0deg)' },
          { transform: 'scale(1.2) rotate(-15deg)', offset: 0.1 },
          { transform: 'scale(1.2) rotate(15deg)',  offset: 0.2 },
          { transform: 'scale(1.2) rotate(-12deg)', offset: 0.3 },
          { transform: 'scale(1.2) rotate(12deg)',  offset: 0.4 },
          { transform: 'scale(1.2) rotate(-8deg)',  offset: 0.5 },
          { transform: 'scale(1.2) rotate(8deg)',   offset: 0.6 },
          { transform: 'scale(1.2) rotate(-4deg)',  offset: 0.7 },
          { transform: 'scale(1.2) rotate(4deg)',   offset: 0.8 },
          { transform: 'scale(1) rotate(0deg)',     offset: 1 }
        ],
        { duration: 900, easing: 'cubic-bezier(0.455, 0.03, 0.515, 0.955)', fill: 'none' }
      );
    });
  }

  init() {
    // Immediately detect market synchronously to prevent race conditions
    this.currentMarket = this.getCurrentMarket();
    this.marketDetectionReady = true;

    // Set up market change detection
    this.setupMarketChangeDetection();
    
    // Listen to cart events to automatically cache cart data
    this.setupCartEventListening();
    
    // Dispatch ready event
    document.dispatchEvent(new CustomEvent('market-detection-ready'));
  }

  /**
   * Set up listeners for cart events to automatically cache cart data
   */
  setupCartEventListening() {
    // Listen for cart events and cache the cart data
    document.addEventListener('cart:updated', (event) => {
      if (event.detail && event.detail.cart) {
        this.setEventCartData(event.detail.cart);
      }
    });
    
    document.addEventListener('cart:refresh', (event) => {
      if (event.detail && event.detail.cart) {
        this.setEventCartData(event.detail.cart);
      }
    });
    
    document.addEventListener('on:line-item:change', (event) => {
      if (event.detail && event.detail.cart) {
        this.setEventCartData(event.detail.cart);
      }
    });
    
    document.addEventListener('on:cart:add', (event) => {
      if (event.detail && event.detail.cart) {
        this.setEventCartData(event.detail.cart);
      }
    });
  }

  /**
   * Set up market change detection to clear caches when market changes
   */
  setupMarketChangeDetection() {
    const lastMarketKey = 'last-detected-market';
    const lastMarket = localStorage.getItem(lastMarketKey);
    const currentMarket = this.getCurrentMarket();
    
    if (lastMarket && lastMarket !== currentMarket) {
      // Clear all cart caches when market changes
      this.clearAllCartCaches();
      
      // Dispatch market change event
      document.dispatchEvent(new CustomEvent('market:changed', {
        detail: { 
          from: lastMarket, 
          to: currentMarket 
        }
      }));
    }
    
    // Store current market
    localStorage.setItem(lastMarketKey, currentMarket);
    
    // Set up periodic market checking
    setInterval(() => {
      const newMarket = this.getCurrentMarket();
      if (newMarket !== this.currentMarket) {
        this.currentMarket = newMarket;
        this.clearAllCartCaches();
        
        document.dispatchEvent(new CustomEvent('market:changed', {
          detail: { 
            from: this.currentMarket, 
            to: newMarket 
          }
        }));
      }
    }, 2000); // Check every 2 seconds
  }

  /**
   * Wait for market detection to be ready
   * @returns {Promise<void>}
   */
  async waitForMarketDetection() {
    // No longer waiting for MarketCartValidator - use direct detection
    return Promise.resolve();
  }

  /**
   * Get current market with fallback
   * @returns {string} Market code (e.g., 'GB', 'FR', 'DE')
   */
  getCurrentMarket() {
    // Direct market detection without dependencies
    let market = null;
    const sources = [];

    // Method 1: Check Shopify object
    if (window.Shopify?.country) {
      market = window.Shopify.country;
      sources.push(`Shopify.country: ${market}`);
    }

    // Method 2: Check localization data
    if (!market && window.localization?.country?.iso_code) {
      market = window.localization.country.iso_code;
      sources.push(`localization.country: ${market}`);
    }

    // Method 3: Check URL pattern (e.g. /de-de/, /fr-fr/)
    if (!market) {
      const urlMatch = window.location.pathname.match(/^\/([a-z]{2})-([a-z]{2})\//);
      if (urlMatch) {
        market = urlMatch[2].toUpperCase();
        sources.push(`URL path: ${urlMatch[0]}`);
      }
    }

    // Method 4: Check document data attributes
    if (!market) {
      const dataMarket = document.documentElement.getAttribute('data-market') || 
                        document.documentElement.getAttribute('data-country');
      if (dataMarket) {
        market = dataMarket;
        sources.push(`Data attribute: ${dataMarket}`);
      }
    }

    // Method 5: Check hostname for subdomain
    if (!market) {
      const hostname = window.location.hostname;
      if  (hostname.includes('.de')) {
        market = 'DE';
        sources.push(`Hostname: ${hostname}`);
      } else if (hostname.includes('.at')) {
        market = 'AT';
        sources.push(`Hostname: ${hostname}`);
      } else if (hostname.includes('.ie')) {
        market = 'IE';
        sources.push(`Hostname: ${hostname}`);
      }
    }

    // Method 6: Check URL parameters
    if (!market) {
      const urlParams = new URLSearchParams(window.location.search);
      const marketParam = urlParams.get('market');
      if (marketParam) {
        market = marketParam.toUpperCase();
        sources.push(`URL param: ${marketParam}`);
      }
    }

    // Method 7: Check theme object if available
    if (!market && typeof theme !== 'undefined' && theme.localization) {
      market = theme.localization.country.iso_code;
      sources.push(`theme.localization: ${market}`);
    }

    // Method 8: Check for Shopify market in meta tags
    if (!market) {
      const marketMeta = document.querySelector('meta[name="shopify-market"]');
      if (marketMeta) {
        market = marketMeta.getAttribute('content');
        sources.push(`Meta tag: ${market}`);
      }
    }

    // Method 9: Check for country in meta tags
    if (!market) {
      const countryMeta = document.querySelector('meta[name="shopify-country"]');
      if (countryMeta) {
        market = countryMeta.getAttribute('content');
        sources.push(`Country meta: ${market}`);
      }
    }

    // Method 10: Check for locale in document
    if (!market) {
      const locale = document.documentElement.getAttribute('lang');
      if (locale) {
        const localeMatch = locale.match(/^([a-z]{2})/);
        if (localeMatch) {
          const countryCode = localeMatch[1].toUpperCase();
          // Map common locale codes to countries
          const localeMap = {
            'FR': 'FR',
            'DE': 'DE', 
            'AT': 'AT',
            'IE': 'IE',
            'ES': 'ES',
            'IT': 'IT',
            'NL': 'NL',
            'PL': 'PL',
            'RO': 'RO',
            'SK': 'SK',
            'SL': 'SL',
            'LT': 'LT'
          };
          if (localeMap[countryCode]) {
            market = localeMap[countryCode];
            sources.push(`Locale: ${locale}`);
          }
        }
      }
    }

    const finalMarket = market?.toUpperCase() || 'GB';
    

    return finalMarket;
  }

  /**
   * Get market-aware URL prefix
   * @returns {string} URL prefix for current market
   */
  getMarketPrefix() {
    if (typeof window !== 'undefined' && window.Shopify
        && window.Shopify.routes && window.Shopify.routes.root) {
      return window.Shopify.routes.root.replace(/\/+$/, ''); // "/en-ie/" -> "/en-ie", "/" -> ""
    }
    return '';
  }

  /**
   * Get market-aware cart API URL (for GET requests only)
   * @param {string} endpoint - Cart API endpoint (e.g., '/cart.js')
   * @returns {string} Market-aware cart API URL
   */
  getMarketAwareCartUrl(endpoint) {
    // Only apply market prefix to GET requests (data fetching)
    if (endpoint.includes('/cart.js') || endpoint.includes('?sections=')) {
      const prefix = this.getMarketPrefix();
      return `${prefix}${endpoint}`;
    }
    // POST requests don't need market prefix
    return endpoint;
  }

  /**
   * Get market-aware sections URL
   * @param {Array<string>} sections - Array of section IDs
   * @returns {string} Market-aware sections URL
   */
  getMarketAwareSectionsUrl(sections) {
    const prefix = this.getMarketPrefix();
    return `${prefix}?sections=${sections.join(',')}`;
  }

  /**
   * Store cart data from event for reuse
   * @param {Object} cartData - Cart data from event
   */
  setEventCartData(cartData) {
    this.eventCartData = cartData;
    this.eventCartDataTime = Date.now();
  }

  /**
   * Get cart data from recent event if available
   * @returns {Object|null} Cart data or null if expired/not available
   */
  getEventCartData() {
    const now = Date.now();
    if (this.eventCartData && (now - this.eventCartDataTime) < this.eventCartDataExpiry) {
      return this.eventCartData;
    }
    return null;
  }

  /**
   * Get market-aware cart data with caching and global throttling
   * @param {boolean} force - Bypass cache
   * @param {Object} eventCartData - Optional cart data from event to use instead of fetching
   * @returns {Promise<Object>} Cart data
   */
  async getMarketAwareCartData(force = false, eventCartData = null) {
    // First, check if event data was provided
    if (eventCartData) {
      this.setEventCartData(eventCartData);
      return eventCartData;
    }

    // Check event data cache
    if (!force) {
      const eventData = this.getEventCartData();
      if (eventData) {
        return eventData;
      }
    }

    // Market detection is now synchronous, no need to wait
    const currentMarket = this.getCurrentMarket();
    const cacheKey = `cart-data-${currentMarket}`;
    const now = Date.now();
    const cacheExpiry = 10000; // Reduced to 10 seconds for better currency sync

    // Check session storage cache
    if (!force) {
      const cached = sessionStorage.getItem(cacheKey);
      if (cached) {
        const { data, timestamp, market } = JSON.parse(cached);
        if (now - timestamp < cacheExpiry && market === currentMarket) {
          return data;
        }
      }
    }

    // Global throttling: if a request is pending, wait for it
    if (this.pendingRequest) {
      return await this.pendingRequest;
    }

    // Global throttling: respect minimum interval between requests
    const timeSinceLastRequest = now - this.lastRequestTime;
    if (timeSinceLastRequest < this.minRequestInterval) {
      await new Promise(resolve => setTimeout(resolve, this.minRequestInterval - timeSinceLastRequest));
    }

    // Create pending request promise
    this.pendingRequest = (async () => {
      try {
        // Fetch fresh data
        const url = this.getMarketAwareCartUrl('/cart.js');
        const response = await fetch(url, { credentials: 'same-origin' });
        
        if (!response.ok) {
          throw new Error(`Cart fetch failed: ${response.status}`);
        }

        const data = await response.json();
        
        // Validate currency matches expected market currency
        const expectedCurrency = this.getExpectedCurrencyForMarket(currentMarket);
        
        // Cache the data with market info
        sessionStorage.setItem(cacheKey, JSON.stringify({
          data,
          timestamp: Date.now(),
          market: currentMarket
        }));

        // Also store in event cache
        this.setEventCartData(data);

        this.lastRequestTime = Date.now();
        return data;
      } finally {
        this.pendingRequest = null;
      }
    })();

    return await this.pendingRequest;
  }

  /**
   * Fetch market-aware sections
   * @param {Array<string>} sections - Array of section IDs
   * @returns {Promise<Object>} Sections data
   */
  async fetchMarketAwareSections(sections) {
    // Market detection is now synchronous, no need to wait
    const currentMarket = this.getCurrentMarket();
    const url = this.getMarketAwareSectionsUrl(sections);
    
    const response = await fetch(url, { credentials: 'same-origin' });
    
    if (!response.ok) {
      throw new Error(`Sections fetch failed: ${response.status}`);
    }

    const data = await response.json();
    
    return data;
  }

  /**
   * Clear cart cache for current market
   */
  clearCartCache() {
    const cacheKey = `cart-data-${this.getCurrentMarket()}`;
    sessionStorage.removeItem(cacheKey);
  }

  /**
   * Clear all cart caches (for market switches)
   */
  clearAllCartCaches() {
    const keys = Object.keys(sessionStorage);
    keys.forEach(key => {
      if (key.startsWith('cart-data-')) {
        sessionStorage.removeItem(key);
      }
    });
  }

  /**
   * Validate cart data currency matches current market
   * @param {Object} cartData - Cart data to validate
   * @returns {boolean} True if currency is correct for current market
   */
  validateCartCurrency(cartData) {
    const currentMarket = this.getCurrentMarket();
    const expectedCurrency = this.getExpectedCurrencyForMarket(currentMarket);
    const actualCurrency = cartData.currency;
    
    const isValid = actualCurrency === expectedCurrency;
        
    return isValid;
  }

  /**
   * Get expected currency for a market
   * @param {string} market - Market code (e.g., 'GB', 'FR', 'DE')
   * @returns {string} Expected currency code
   */
  getExpectedCurrencyForMarket(market) {
    const currencyMap = {
      'GB': 'GBP',
      'US': 'USD',
      'FR': 'EUR',
      'DE': 'EUR',
      'AT': 'EUR',
      'IE': 'EUR'
    };
    
    return currencyMap[market] || 'GBP';
  }

  /**
   * Force refresh cart data if currency is incorrect
   * @param {Object} cartData - Current cart data
   * @returns {Promise<Object>} Fresh cart data if currency was wrong
   */
  async ensureCorrectCurrency(cartData) {
    if (!this.validateCartCurrency(cartData)) {
      return await this.getMarketAwareCartData(true);
    }
    
    return cartData;
  }

  /**
   * Force clear all caches and fetch fresh cart data
   * Used when page loads to ensure correct currency from start
   * @returns {Promise<Object>} Fresh cart data
   */
  async forceFreshCartData() {
    // Clear all caches
    this.clearAllCartCaches();

    // Force fresh fetch
    const cartData = await this.getMarketAwareCartData(true);

    return cartData;
  }

  /**
   * Safely parse JSON response, handling HTML error pages
   * @param {Response} response - Fetch response
   * @returns {Promise<Object>} Parsed JSON or error object
   */
  async safeParseJson(response) {
    const contentType = response.headers.get('content-type') || '';
    const text = await response.text();

    // Check if response is HTML (error page)
    if (contentType.includes('text/html') || text.trim().startsWith('<!DOCTYPE') || text.trim().startsWith('<html')) {
      return {
        _isHtmlError: true,
        status: response.status,
        message: response.status === 429 ? 'Rate limited - too many requests' : `Server returned HTML (status ${response.status})`
      };
    }

    try {
      return JSON.parse(text);
    } catch (e) {
      return {
        _isParseError: true,
        status: response.status,
        message: `Failed to parse response: ${e.message}`,
        raw: text.substring(0, 200)
      };
    }
  }

  /**
   * Fetch with automatic retry on 429 errors
   * @param {string} url - URL to fetch
   * @param {Object} options - Fetch options
   * @param {number} maxRetries - Maximum retry attempts
   * @returns {Promise<{response: Response, data: Object}>}
   */
  async fetchWithRetry(url, options = {}, maxRetries = 3) {
    let lastError = null;
    let retryCount = 0;

    while (retryCount <= maxRetries) {
      try {
        // Wait for backoff if we've had errors
        if (retryCount > 0) {
          const backoffTime = Math.min(this.rateLimitBackoff * retryCount, this.maxBackoff);
          await new Promise(resolve => setTimeout(resolve, backoffTime));
        }

        const response = await fetch(url, {
          credentials: 'same-origin',
          ...options
        });

        const data = await this.safeParseJson(response);

        // Handle rate limiting
        if (response.status === 429 || data._isHtmlError && data.status === 429) {
          this.consecutiveErrors++;
          lastError = new Error('Rate limited (429)');
          lastError.status = 429;
          retryCount++;
          continue;
        }

        // Handle other errors
        if (!response.ok) {
          const errorMessage = data.message || data.description || `Request failed: ${response.status}`;
          lastError = new Error(errorMessage);
          lastError.status = response.status;
          lastError.data = data;

          if (response.status === 422
            && this.isCartWideCheckoutValidationError(lastError, errorMessage)) {
            this.checkoutValidationBlockMessage = errorMessage;
          }

          // Don't retry client errors (429 is handled above with continue).
          if (response.status >= 400 && response.status < 500) {
            throw lastError;
          }

          retryCount++;
          continue;
        }

        // Success - reset error tracking
        this.consecutiveErrors = 0;
        this.checkoutValidationBlockMessage = null;
        return { response, data };

      } catch (error) {
        // Thrown 4xx (e.g. 422 checkout validation) must not be retried — the
        // outer catch used to loop up to maxRetries+1 times per click.
        if (error?.status >= 400 && error.status < 500) {
          throw error;
        }
        lastError = error;
        retryCount++;
      }
    }

    // All retries exhausted
    throw lastError || new Error('Request failed after retries');
  }

  /**
   * Whether an error is Shopify rate limiting (429), for customer messaging.
   * @param {Error|string} errorOrMessage
   * @returns {boolean}
   */
  isRateLimitCartError(errorOrMessage) {
    if (!errorOrMessage) return false;
    if (typeof errorOrMessage === 'object' && errorOrMessage.name === 'InventoryLimitError') {
      return false;
    }
    if (typeof errorOrMessage === 'object' && errorOrMessage.status === 429) return true;
    const message = String(
      typeof errorOrMessage === 'string' ? errorOrMessage : errorOrMessage.message || ''
    ).trim();
    if (!message) return false;
    // Shopify inventory failures sometimes use a bare max-qty number (e.g. "429").
    if (/^[0-9]+$/.test(message)) {
      return typeof errorOrMessage === 'object' && errorOrMessage.status === 429;
    }
    return /rate limit|too many requests|sections fetch failed:\s*429|\(429\)/i.test(message);
  }

  /**
   * Message safe to show in the cart UI (hides raw 429 / status codes).
   * @param {Error|string} errorOrMessage
   * @returns {string}
   */
  getCustomerFacingCartError(errorOrMessage) {
    if (this.isRateLimitCartError(errorOrMessage)) {
      const fallback = 'Cart error: please reload page';
      return (typeof theme !== 'undefined' && theme.strings && theme.strings.cartReloadError) || fallback;
    }
    if (typeof errorOrMessage === 'string') return errorOrMessage;
    return errorOrMessage?.message || '';
  }

  /**
   * Checkout cart validation (e.g. member points) applies to the whole cart, not
   * the line whose quantity the customer changed. Show those in .cart-errors.
   * @param {Error|string} errorOrMessage
   * @param {string} [displayMessage] - Already normalized customer-facing text
   * @returns {boolean}
   */
  isCartWideCheckoutValidationError(errorOrMessage, displayMessage) {
    if (!errorOrMessage) return false;
    if (typeof errorOrMessage === 'object' && errorOrMessage.name === 'InventoryLimitError') {
      return false;
    }

    const message = String(
      displayMessage
        || (typeof errorOrMessage === 'string'
          ? errorOrMessage
          : errorOrMessage.message || '')
    ).trim();
    if (!message) return false;

    if (/^[0-9]+$/.test(message)) return false;

    if (/member points|papa points/i.test(message)) return true;

    const status = typeof errorOrMessage === 'object' ? errorOrMessage.status : undefined;
    return status === 422;
  }

  /**
   * Papa (member exclusive) lines — product vendor PAP, not line properties.
   * @param {Object} item - Cart line from /cart.js
   * @returns {boolean}
   */
  isPapaLine(item) {
    return !!item && item.vendor === 'PAP';
  }

  /**
   * Checkout button total for the cart drawer: eligible line items minus
   * cart-level discounts (matches snippets/cart-drawer.liquid).
   * Includes hidden vendor PAP lines.
   * @param {Object} cartData - Cart JSON from /cart.js
   * @returns {number} Total in cents
   */
  getDrawerCheckoutTotalCents(cartData) {
    if (!cartData || !Array.isArray(cartData.items)) return 0;

    let totalCents = 0;
    for (const item of cartData.items) {
      const props = item.properties || {};
      const isFreeCan = props._free_can === true || props._free_can === 'true';
      if (isFreeCan && props._free_can_type === 'bundle_product') continue;

      const original = Number(item.original_line_price) || 0;
      const final = Number(item.final_line_price);
      const lineCents = final != null && !Number.isNaN(final) && original !== final ? final : original;
      totalCents += lineCents;
    }

    const discounts = cartData.cart_level_discount_applications || [];
    for (const discount of discounts) {
      totalCents -= Number(discount.total_allocated_amount) || 0;
    }

    return Math.max(0, totalCents);
  }

  /**
   * Total quantity of a variant already sitting in the cart, across every line
   * item (the same variant can appear more than once with different properties).
   * @param {Object} cartData - Cart JSON
   * @param {number|string} variantId - Variant ID
   * @returns {number} Quantity already in cart
   */
  getCartQuantityForVariant(cartData, variantId) {
    if (!cartData || !Array.isArray(cartData.items)) return 0;
    const id = parseInt(variantId, 10);
    return cartData.items.reduce(
      (total, item) => (item.variant_id === id ? total + (parseInt(item.quantity, 10) || 0) : total),
      0
    );
  }

  /**
   * Throws before an add that would put more units of a variant in the cart than
   * exist in stock. Shopify only catches this at checkout, so without it a
   * shopper can add a 5-pack of a 7-stock product twice and reach the checkout
   * with a cart of 10.
   *
   * Deliberately cheap: it no-ops unless the caller supplies a tracked, low
   * stock level, so the overwhelming majority of adds pay nothing at all.
   *
   * @param {number|string} variantId - Variant ID being added
   * @param {number} quantity - Quantity being added
   * @param {number} available - Tracked stock level, or null/undefined to skip
   * @throws {Error} name === 'InventoryLimitError' when the add would oversell
   */
  async assertInventoryAvailable(variantId, quantity, available) {
    const stock = Number(available);
    // No hint, untracked inventory, or `continue` policy (backorders allowed):
    // there is nothing to enforce, let the server decide.
    if (available === null || available === undefined || !Number.isFinite(stock)) return;
    // Plenty in stock - not worth a cart read.
    if (stock > this.inventoryGuardThreshold) return;

    let cartData = this.getEventCartData();
    if (!cartData) {
      // Force a fresh read: a stale cache would silently let the oversell
      // through, which is the exact bug this guard exists to close. In-flight
      // requests are still shared, so concurrent adds cost one fetch between them.
      cartData = await this.getMarketAwareCartData(true);
    }
    if (!cartData) return; // Cart unreadable - don't block the sale, server still validates.

    // Adds already cleared but not yet reflected in /cart.js count as in-cart,
    // otherwise two clicks in quick succession both measure against empty stock.
    const inCart = this.getCartQuantityForVariant(cartData, variantId)
      + (this.pendingAddQuantities.get(String(variantId)) || 0);
    const requested = parseInt(quantity, 10) || 1;
    if (inCart + requested <= stock) return;

    const remaining = Math.max(0, stock - inCart);
    const error = new Error(
      inCart > 0
        ? `Only ${stock} left in stock and you already have ${inCart} in your cart.`
        : `Only ${stock} left in stock.`
    );
    error.name = 'InventoryLimitError';
    error.inventoryLimit = { available: stock, inCart, requested, remaining };
    throw error;
  }

  /**
   * Add item to cart with queue management
   * Prevents duplicate requests and handles rate limiting
   * @param {number|string} variantId - Variant ID to add
   * @param {number} quantity - Quantity to add
   * @param {Object} properties - Line item properties
   * @param {Object} options - Additional options (sections, sections_url, inventoryAvailable)
   * @returns {Promise<Object>} Cart data after addition
   */
  async addToCart(variantId, quantity = 1, properties = {}, options = {}) {
    // `inventoryAvailable` is a guard-only hint - strip it so it never reaches
    // the /cart/add.js payload.
    const { inventoryAvailable, ...addOptions } = options;

    if (this.checkoutValidationBlockMessage) {
      const blocked = new Error(this.checkoutValidationBlockMessage);
      blocked.status = 422;
      throw blocked;
    }

    // Runs before the optimistic UI below so a blocked add never bumps the badge.
    await this.assertInventoryAvailable(variantId, quantity, inventoryAvailable);

    const playAddSuccessFeedback = () => {
      try {
        const isUpsell = properties && (properties._upsell_item === 'true' || properties._free_can === 'true');
        const isQtyRestricted = properties && properties._quantity_restricted === 'true';
        if (!isUpsell && !isQtyRestricted) {
          document.querySelectorAll('[id="cart-count-badge"]').forEach((badge) => {
            const countEl = badge.querySelector('.cart-count-number');
            if (countEl) {
              const current = parseInt(countEl.textContent, 10) || 0;
              countEl.textContent = current + (parseInt(quantity, 10) || 1);
            }
            badge.style.visibility = '';
          });
        }
        const icons = document.querySelectorAll('[id="cart-icon"]');
        icons.forEach((icon) => {
          try { icon.getAnimations && icon.getAnimations().forEach((a) => a.cancel()); } catch (e) {}
          icon.style.transformOrigin = 'center center';
          icon.animate(
            [
              { transform: 'scale(1) rotate(0deg)' },
              { transform: 'scale(1.2) rotate(-15deg)', offset: 0.1 },
              { transform: 'scale(1.2) rotate(15deg)',  offset: 0.2 },
              { transform: 'scale(1.2) rotate(-12deg)', offset: 0.3 },
              { transform: 'scale(1.2) rotate(12deg)',  offset: 0.4 },
              { transform: 'scale(1.2) rotate(-8deg)',  offset: 0.5 },
              { transform: 'scale(1.2) rotate(8deg)',   offset: 0.6 },
              { transform: 'scale(1.2) rotate(-4deg)',  offset: 0.7 },
              { transform: 'scale(1.2) rotate(4deg)',   offset: 0.8 },
              { transform: 'scale(1) rotate(0deg)',     offset: 1 }
            ],
            { duration: 900, easing: 'cubic-bezier(0.455, 0.03, 0.515, 0.955)', fill: 'none' }
          );
        });
      } catch (e) {
        console.warn('[MarketCartUtils.addToCart] shake/badge feedback failed', e);
      }
    };

    const operationKey = `add-${variantId}-${quantity}-${JSON.stringify(properties)}`;

    // Check for duplicate pending operation
    if (this.pendingAddOperations.has(operationKey)) {
      return await this.pendingAddOperations.get(operationKey);
    }

    // Reserve this quantity against stock for the lifetime of the request, so a
    // second add of the same variant is measured against what's already on its
    // way to the cart rather than only what /cart.js currently reports.
    // Note: Number(null) is 0, so null/undefined must be excluded explicitly.
    const isGuarded = inventoryAvailable !== null
      && inventoryAvailable !== undefined
      && Number.isFinite(Number(inventoryAvailable));
    const guardedKey = isGuarded ? String(variantId) : null;
    if (guardedKey) {
      this.pendingAddQuantities.set(
        guardedKey,
        (this.pendingAddQuantities.get(guardedKey) || 0) + (parseInt(quantity, 10) || 1)
      );
    }

    const operation = this.queueMutation(async () => {
      const payload = {
        id: variantId,
        quantity: Number(quantity),
        ...addOptions
      };

      if (Object.keys(properties).length > 0) {
        payload.properties = properties;
      }

      const url = window.Shopify?.routes?.root
        ? `${window.Shopify.routes.root}cart/add.js`
        : '/cart/add.js';

      const { data } = await this.fetchWithRetry(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      // Clear cache after mutation
      this.clearCartCache();
      this.eventCartData = null;

      return data;
    });

    // Track this operation to prevent duplicates
    this.pendingAddOperations.set(operationKey, operation);

    try {
      const result = await operation;
      this.checkoutValidationBlockMessage = null;
      playAddSuccessFeedback();
      return result;
    } finally {
      this.pendingAddOperations.delete(operationKey);
      if (guardedKey) {
        const remainingPending = (this.pendingAddQuantities.get(guardedKey) || 0) - (parseInt(quantity, 10) || 1);
        if (remainingPending > 0) {
          this.pendingAddQuantities.set(guardedKey, remainingPending);
        } else {
          this.pendingAddQuantities.delete(guardedKey);
        }
      }
    }
  }

  /**
   * Change cart item quantity with queue management
   * @param {string|number} id - Cart item key or ID
   * @param {number} quantity - New quantity
   * @param {Object} options - Additional options (sections, sections_url)
   * @returns {Promise<Object>} Updated cart data
   */
  async changeCartItem(id, quantity, options = {}) {
    return this.queueMutation(async () => {
      const payload = {
        id: id,
        quantity: Number(quantity),
        ...options
      };

      const { data } = await this.fetchWithRetry('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      // Update cache with new cart data
      this.setEventCartData(data);

      return data;
    });
  }

  /**
   * Update multiple cart items at once
   * @param {Object} updates - Object mapping line item keys to quantities
   * @param {Object} options - Additional options
   * @returns {Promise<Object>} Updated cart data
   */
  async updateCart(updates, options = {}) {
    return this.queueMutation(async () => {
      const payload = {
        updates: updates,
        ...options
      };

      const { data } = await this.fetchWithRetry('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      // Update cache with new cart data
      this.setEventCartData(data);

      return data;
    });
  }

  /**
   * Set cart-level attributes (e.g. Papa apply-discounts opt-in).
   * @param {Object} attributes - Key/value cart attributes
   * @param {Object} options - sections, sections_url, etc.
   * @returns {Promise<Object>} Updated cart JSON
   */
  async setCartAttributes(attributes, options = {}) {
    return this.queueMutation(async () => {
      const payload = {
        attributes,
        ...options,
      };

      const { data } = await this.fetchWithRetry('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(payload),
      });

      this.setEventCartData(data);
      return data;
    });
  }

  /**
   * Queue a cart mutation to prevent race conditions
   * Only one mutation can run at a time
   * @param {Function} mutationFn - Async function that performs the mutation
   * @returns {Promise<any>} Result of the mutation
   */
  async queueMutation(mutationFn) {
    return new Promise((resolve, reject) => {
      this.mutationQueue.push({ mutationFn, resolve, reject });
      this.processMutationQueue();
    });
  }

  /**
   * Process the mutation queue sequentially
   */
  async processMutationQueue() {
    if (this.isProcessingMutation || this.mutationQueue.length === 0) {
      return;
    }

    this.isProcessingMutation = true;

    while (this.mutationQueue.length > 0) {
      const { mutationFn, resolve, reject } = this.mutationQueue.shift();

      // Add delay between mutations to prevent rate limiting
      if (this.lastRequestTime > 0) {
        const timeSinceLastRequest = Date.now() - this.lastRequestTime;
        if (timeSinceLastRequest < this.minRequestInterval) {
          await new Promise(r => setTimeout(r, this.minRequestInterval - timeSinceLastRequest));
        }
      }

      try {
        const result = await mutationFn();
        this.lastRequestTime = Date.now();
        resolve(result);
      } catch (error) {
        this.lastRequestTime = Date.now();
        reject(error);
      }
    }

    this.isProcessingMutation = false;
  }

  /**
   * Check if a cart operation is currently in progress
   * @returns {boolean}
   */
  isCartBusy() {
    return this.isProcessingMutation || this.mutationQueue.length > 0;
  }

  /**
   * Get the number of pending operations
   * @returns {number}
   */
  getPendingOperationCount() {
    return this.mutationQueue.length + (this.isProcessingMutation ? 1 : 0);
  }

  /**
   * Update the cart icon bubble from section HTML response
   * Extracts inner content from shopify-section wrapper to maintain consistent DOM structure
   * @param {string} sectionHtml - The section HTML from Shopify response
   * @returns {boolean} - Whether the update was successful
   */
  updateCartIconBubble(sectionHtml) {
    if (!sectionHtml) return false;

    // The header section may be rendered multiple times (e.g. one per market),
    // producing duplicate IDs. Update every #cart-icon-bubble in the DOM.
    const bubbles = document.querySelectorAll('[id="cart-icon-bubble"]');
    if (!bubbles.length) return false;

    // Extract inner content from shopify-section wrapper
    const tmpl = document.createElement('template');
    tmpl.innerHTML = sectionHtml;
    const sectionWrapper = tmpl.content.querySelector('.shopify-section');
    const innerHtml = sectionWrapper ? sectionWrapper.innerHTML : sectionHtml;

    bubbles.forEach((bubble) => {
      bubble.innerHTML = innerHtml;
    });

    return true;
  }

}

// Create global instance
window.MarketCartUtils = new MarketCartUtils();

// Single page load cart initialization - fetches once and dispatches event for all listeners
(function initializeCartOnPageLoad() {
  // Only run once
  if (window._cartInitialized) return;
  window._cartInitialized = true;

  // Wait for DOM ready
  const initialize = async () => {
    try {
      // Fetch cart data once
      let cartData = await window.MarketCartUtils.getMarketAwareCartData();

      // Drop loyalty lines this market is not selling.
      // Guests never keep free cans or PAP.
      // Logged-in: free cans only while market_loyalty_viscon is'1';
      // PAP only while tech_papa_viscon is '1'.
      if (cartData && Array.isArray(cartData.items)) {
        const loyaltyOn = String(window.marketLoyaltyViscon ?? '').trim() == '1';
        const papaOn = String(window.techPapaViscon ?? '').trim() === '1';
        const stripFreeCans = !window.customerLoggedIn || !loyaltyOn;
        const stripPapa = !window.customerLoggedIn || !papaOn;

        const loyaltyKeys = cartData.items
          .filter(item => {
            if (stripPapa && window.MarketCartUtils.isPapaLine(item)) return true;
            if (!stripFreeCans || !item.properties) return false;
            return item.properties._free_can === true || item.properties._free_can === 'true';
          })
          .map(item => item.key);

        if (loyaltyKeys.length > 0) {
          const updates = loyaltyKeys.reduce((acc, key) => { acc[key] = 0; return acc; }, {});
          try {
            cartData = await window.MarketCartUtils.updateCart(updates);
          } catch (cleanupError) {
            console.error('Error removing loyalty lines disabled for this market:', cleanupError);
          }
        }
      }

      // Cache it for any components that check before the event
      window.MarketCartUtils.setEventCartData(cartData);

      // Dispatch event for all components to consume
      document.dispatchEvent(new CustomEvent('cart:initialized', {
        bubbles: true,
        detail: { cart: cartData }
      }));
    } catch (error) {
      console.error('Error initializing cart data:', error);
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize);
  } else {
    // DOM already ready, initialize after a microtask to ensure components are connected
    Promise.resolve().then(initialize);
  }
})();

// Export for module systems
if (typeof module !== 'undefined' && module.exports) {
  module.exports = MarketCartUtils;
}
